<template>
  <div v-if="!loading">
    <div v-if="!requestedChangeOrder">
      <app-fieldset :title="i18n.t('/change-order/forms/print-pdf-title')">
        <div v-if="sendEmailToCustomer || sendCopyToEmployee" class="customer-message-field">
          <label for="customerMessage">{{
            i18n.t('/change-order/forms/customer-message-label')
          }}</label>
          <textarea
            id="customerMessage"
            v-model="customerMessage"
            rows="8"
            :placeholder="i18n.t('/change-order/forms/customer-message-placeholder')"
            class="customer-message-textarea"
          />
        </div>

        <form-checkbox id="sendEmailToCustomer" :title="mailCustomerLabel">
          <input
            id="sendEmailToCustomer"
            v-model="sendEmailToCustomer"
            type="checkbox"
            name="sendEmailToCustomer"
          />
        </form-checkbox>

        <form-checkbox id="sendCopyToEmployee" :title="mailCopyToEmployee">
          <input
            id="sendCopyToEmployee"
            v-model="sendCopyToEmployee"
            type="checkbox"
            name="sendCopyToEmployee"
          />
        </form-checkbox>

        <form-checkbox
          v-if="digitalSigningEnabled && isSigningStatusEmpty"
          id="signDigitally"
          :title="i18n.t('/QuoteActions/Forms/DigitalSigning/SignDigitally')"
        >
          <input id="signDigitally" v-model="signDigitally" type="checkbox" name="signDigitally" />
        </form-checkbox>
      </app-fieldset>

      <div class="print-pdf-button-container">
        <download-change-order-button :change-order-id="currentChangeOrder?.ChangeOrderId" />

        <app-button
          :disabled="loading"
          data-qa="send-change-order-form-button"
          button-type="cta"
          class="submit-button"
          @click="submit"
        >
          {{ i18n.t('/Common/Send') }}
        </app-button>
      </div>

      <modal
        v-if="showDigitalSigningModal"
        :title="i18n.t('/change-order/forms/print-pdf-title')"
        @close="toggleDigitalSigningModal(false)"
      >
        <digital-signing-form
          :entity-type="entityType.changeOrder"
          @close="toggleDigitalSigningModal(false)"
          @submit-digital-signing="submitDigitalSigning"
        />
      </modal>
      <digital-login-redirect-form
        :show="showDigitalLoginRedirectModal"
        :digital-redirect-url="digitalRedirectUrl"
        @close="toggleDigitalLoginRedirectModal(false)"
      />
    </div>

    <div v-else>
      <div v-if="!hasErrors">
        <p>
          {{ i18n.t('/change-order/forms/success-order-header-title') }}
        </p>

        <!-- Local signing QR code section -->
        <div v-if="showLocalSigningUrlButton && !goPrinterEnabled">
          <p>
            {{ i18n.t('/QuoteActions/Forms/DigitalSigning/ScanQrCodeText') }}
          </p>

          <div class="signing-qr-code-wrapper">
            <img class="signing-qr-code" :src="signingQrCodeImageSrc" />
          </div>
        </div>
      </div>
      <div v-else>
        <!-- errors -->
        <p>{{ i18n.t('/change-order/forms/error-text') }}</p>
        <pre v-for="(error, index) in requestChangeOrderErrors" :key="index">
          {{ error }}
        </pre>
      </div>

      <!-- Action buttons -->
      <div class="buttons">
        <app-button data-qa="close-change-order-form-button" @click="$emit('close')">
          {{ i18n.t('/Common/Close') }}
        </app-button>
        <app-button
          v-if="showLocalSigningUrlButton"
          data-qa="open-signin-url-button"
          @click="openSigninUrl()"
        >
          {{ i18n.t('/QuoteActions/Forms/DigitalSigning/OpenLocalUrlText') }}
        </app-button>
      </div>
    </div>
  </div>
  <div v-else>
    <loader />
  </div>
</template>

<script>
import AppFieldset from '@/components/generic/presentational/atoms/appFieldset/AppFieldset.vue'
import AppButton from '@/components/generic/presentational/atoms/appButton/AppButton.vue'
import Modal from '@/components/generic/presentational/molecules/modal/Modal.vue'
import FormCheckbox from '@/components/generic/presentational/atoms/formCheckbox/FormCheckbox.vue'
import DigitalSigningForm from '@/components/generic/presentational/organisms/quoteActions/DigitalSigningForm.vue'
import DigitalLoginRedirectForm from '@/components/generic/presentational/organisms/quoteActions/DigitalLoginRedirectForm.vue'
import DigitalSigning_Api from '@/api/server/DigitalSigning.api.js'
import changeOrderService from '@/services/changeOrderService'
import ChangeOrderApi from '@/api/server/ChangeOrder.js'
import StoreConnector from '@/helpers/StoreConnector'
import Loader from '@/components/generic/presentational/atoms/loader/Loader.vue'
import { mapGetters, mapState } from 'vuex'
import entityType from '@/helpers/enums/entityType'
import digitalSigningStatus from '@/helpers/enums/digitalSigningStatus'
import DownloadChangeOrderButton from '@/components/generic/presentational/molecules/downloadButton/DownloadChangeOrderButton.vue'

export default {
  components: {
    DownloadChangeOrderButton,
    AppButton,
    AppFieldset,
    Loader,
    FormCheckbox,
    DigitalSigningForm,
    Modal,
    DigitalLoginRedirectForm,
  },
  emits: ['close', 'signingLocally'],
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,

      sendEmailToCustomer: false,
      sendCopyToEmployee: true,
      customerMessage: '',
      signDigitally: false,
      signManually: false,
      sendEmailOnly: false,
      digitalSigningParams: null,

      requestedChangeOrder: false,
      loading: false,
      showDigitalSigningModal: false,
      showDigitalLoginRedirectModal: false,
      showPrintPdfSummaryModal: false,

      digitalRedirectUrl: null,

      hasErrors: false,
      requestChangeOrderErrors: [], // Store error messages

      // local signing parameters
      signingUrl: '',
      signingQrCode: null,
      signLocally: false,
      goPrinterEnabled: false,
    }
  },
  computed: {
    entityType() {
      return entityType
    },
    ...mapState({
      currentChangeOrder(state, getters) {
        return getters['changeOrder/get']
      },
      isSigningStatusEmpty(state, getters) {
        return getters['changeOrder/changeOrderSigningStatusIsEmpty']
      },
    }),
    ...mapState('user', ['dealerId']),
    ...mapGetters({
      dealerSettings: 'user/dealerSettings',
    }),
    //TODO CST-4230 do something about this:
    signingQrCodeImageSrc() {
      return `data:image/jpeg;base64,${this.signingQrCode}`
    },
    changeOrderId() {
      return this.currentChangeOrder.ChangeOrderId
    },
    customerInfo() {
      return this.currentChangeOrder.Customer
    },
    mailCustomerLabel() {
      if (this.customerInfo?.CustomerEmail != null)
        return `${this.i18n.t('/QuoteActions/Forms/MailConfirmationToCustomerLabel')}: ${
          this.customerInfo?.CustomerEmail
        }`
      else return this.i18n.t('/QuoteActions/Forms/MailConfirmationToCustomerLabel')
    },
    mailCopyToEmployee() {
      return `${this.i18n.t('/QuoteActions/Forms/MailConfirmationCopyLabel')}: ${
        window.info?.user?.UserInfo?.Email
      }`
    },
    showLocalSigningUrlButton() {
      return !this.hasErrors && this.signLocally
    },
    digitalSigningEnabled() {
      return this.dealerSettings?.pdfSigningEnabled
    },
  },
  async created() {
    this.customerMessage = await changeOrderService.getChangeOrderEmailBody(
      this.currentChangeOrder.ChangeOrderId
    )
  },
  methods: {
    openSigninUrl() {
      if (!!this.signingUrl) window.open(this.signingUrl)
    },
    toggleDigitalLoginRedirectModal(show) {
      this.showDigitalLoginRedirectModal = show
    },
    async toggleDigitalSigningModal(show) {
      if (show) {
        let authData = await DigitalSigning_Api.CheckAuthentication()
        let redirectUrl = authData?.Body?.AuthenticationDetails?.AuthorizationUrl
        this.goPrinterEnabled = authData?.Body?.GoPrinterEnabled
        if (redirectUrl) {
          this.digitalRedirectUrl = redirectUrl
          this.toggleDigitalLoginRedirectModal(true)
        } else if (!this.goPrinterEnabled) {
          this.showDigitalSigningModal = show
        } else {
          let digitalSigningParams = {
            signLocally: true,
          }
          this.submitDigitalSigning(digitalSigningParams)
        }
      } else {
        this.showDigitalSigningModal = show
      }
    },
    async submit() {
      if (this.signDigitally) {
        await this.toggleDigitalSigningModal(true)
      } else {
        // For manual/no signing, just process the request
        await this.requestChangeOrderSigning()
      }
    },
    async submitDigitalSigning(digitalSigningParams) {
      this.digitalSigningParams = digitalSigningParams
      await this.toggleDigitalSigningModal(false)
      await this.requestChangeOrderSigning()
    },
    async requestChangeOrderSigning() {
      this.loading = true

      let changeOrderParams = {
        changeOrderId: this.changeOrderId,
        sendEmailToCustomer: this.sendEmailToCustomer,
        sendCopyToEmployee: this.sendCopyToEmployee,
        customerMessage: this.customerMessage,
        digitalSigningParams: this.digitalSigningParams,
        sendSigning: this.signDigitally || this.signManually,
      }

      if (this.sendEmailOnly) {
        changeOrderParams.sendSigning = true
      }

      try {
        this.requestSalesOrderResult =
          await changeOrderService.submitChangeOrderSigning(changeOrderParams)

        // Update signing status after successful PDF submission
        try {
          let newSigningStatus = null

          // Determine the appropriate signing status based on the action taken
          if (this.signDigitally) {
            // Digital signing was requested - system will get feedback when customer signs
            newSigningStatus = digitalSigningStatus.sentToCustomer
          } else if (this.sendEmailToCustomer || this.sendCopyToEmployee) {
            // PDF was sent via email for manual signing - no automated feedback
            // Set to completed immediately (matches SalesOrderForm behavior)
            newSigningStatus = digitalSigningStatus.manualSignCompleted
          }

          // Only update status if a signing action was performed
          if (newSigningStatus) {
            await ChangeOrderApi.changeSigningStatus(this.changeOrderId, newSigningStatus)

            // Update the Vuex store with the new signing status
            const updatedChangeOrder = {
              ...this.currentChangeOrder,
              SigningStatus: newSigningStatus,
            }
            await StoreConnector.dispatch('changeOrder/setChangeOrder', updatedChangeOrder)
          }
        } catch (error) {
          console.error('Failed to update signing status:', error)
          // Don't fail the whole operation if status update fails
          // The PDF was still sent successfully
        }

        this.signLocally = this.digitalSigningParams?.signLocally
        if (this.signLocally) {
          this.signingUrl = await DigitalSigning_Api.getSigningUrl({
            entityId: this.changeOrderId,
            entityType: entityType.changeOrder,
          })

          if (!this.goPrinterEnabled) {
            this.signingQrCode = await DigitalSigning_Api.getSigningQrCode({
              entityId: this.changeOrderId,
              entityType: entityType.changeOrder,
            })
          }

          this.$emit('signingLocally', this.digitalSigningParams.signLocally)
        }

        this.hasErrors = false
        this.requestChangeOrderErrors = []
      } catch (error) {
        console.error('Failed to submit change order signing:', error)
        this.hasErrors = true
        this.requestChangeOrderErrors = [
          error?.response?.data?.ErrorDescription || 'Er is een fout opgetreden',
        ]
      } finally {
        this.loading = false
        this.requestedChangeOrder = true // Show success view

        // Only auto-close if NOT signing locally and no errors
        if (!this.signLocally && !this.hasErrors) {
          this.$emit('close')
        }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
pre {
  white-space: normal;
}

.customer-message-field {
  margin-top: 1rem;
  margin-bottom: 1rem;

  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
  }

  .customer-message-textarea {
    width: 100%;
    padding: 0.5rem;
    border: 1px solid #ccc;
    border-radius: 4px;
    font-family: inherit;
    font-size: 0.875rem;
    resize: vertical;
    height: 300px;

    &:focus {
      outline: none;
      border-color: #007bff;
    }
  }
}

.signing-qr-code-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.875rem;

  .signing-qr-code {
    background-color: lightcoral;
    width: 200px;
    height: 200px;
  }
}

.print-pdf-button-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;
  width: 100%;
}

.buttons {
  display: flex;
  justify-content: space-between;
}

.modal-buttons {
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
}

.buttons {
  display: flex;
  justify-content: space-between;
}
</style>
